/**
 * ISG Marketing Platform — Main Entry Point
 */
import '@isg/ui-kit/css/tokens.css';
import '@isg/ui-kit/css/reset.css';
import '@isg/ui-kit/css/components.css';
import '@isg/ui-kit/css/layout.css';
import './styles/marketing-overrides.css';
import '../node_modules/frappe-gantt/dist/frappe-gantt.css';

import { router, store, auth , checkEnvBanner } from '@isg/ui-kit';
import { renderNavigation, renderMobileHeader, createSidebarOverlay } from '@isg/ui-kit';
import { applyBranding, loadBranding } from '@isg/ui-kit';
import { createNotificationBell, createNotificationSidebar } from './components/notification-sidebar.js';

/* SSO: capture handoff from intranet broker */
auth.captureSSOHandoff();

/**
 * Navigation configuration — Marketing-specific.
 */
const NAV_CONFIG = [
  {
    section: null,
    items: [
      { path: '/', label: 'Dashboard', icon: 'layout-dashboard' },
    ],
  },
  {
    section: 'Plan & deliver',
    items: [
      { path: '/plan', label: 'FY Plan', icon: 'layout-grid' },
      { path: '/worklist', label: 'Work List', icon: 'list-checks' },
      { path: '/my-work', label: 'My Work', icon: 'target' },
      { path: '/tasks', label: 'Tasks', icon: 'check-square' },
      { path: '/workload', label: 'Team Workload', icon: 'users' },
    ],
  },
  {
    section: 'Campaigns',
    items: [
      { path: '/campaigns', label: 'All Campaigns', icon: 'speakerphone' },
      { path: '/archive', label: 'Archive', icon: 'archive' },
      { path: '/plans', label: 'Marketing Plans', icon: 'clipboard-list' },
    ],
  },
  {
    section: 'Finance',
    items: [
      { path: '/budgets', label: 'Budget Manager', icon: 'currency-pound' },
    ],
  },
  {
    section: 'Analytics',
    items: [
      { path: '/web-dashboard', label: 'Marketing Analytics', icon: 'globe' },
      { path: '/market-intel', label: 'Market Intelligence', icon: 'target' },
    ],
  },
  {
    // Shows & Events (2026-10-12): results (Shows) beside the tool (Visitor Forms).
    section: 'Shows & Events',
    items: [
      { path: '/shows', label: 'Shows', icon: 'presentation-chart-line' },
      { path: '/forms', label: 'Visitor Forms', icon: 'file-text' },
    ],
  },
  {
    // CRM execution platform — docs/CRM_PLATFORM_PLAN.md
    section: 'CRM',
    items: [
      { path: '/crm/contacts', label: 'Contacts', icon: 'user-check' },
      { path: '/crm/flows', label: 'Flows', icon: 'git-branch' },
    ],
  },
  {
    section: 'Library',
    items: [
      { path: '/email-templates', label: 'Email Builder', icon: 'mail' },
      { path: '/assets', label: 'Asset Library', icon: 'photo' },
      { path: '/documents', label: 'Documents', icon: 'files' },
      { path: '/platforms', label: 'Platform Matrix', icon: 'grid' },
    ],
  },
  {
    section: 'System',
    items: [
      { path: '/settings', label: 'Settings', icon: 'settings', adminOnly: true },
      { path: '/gtm', label: 'GTM Admin', icon: 'layout', adminOnly: true },
    ],
  },
];

// Lazy-load pages
const pages = {
  dashboard:      () => import('./pages/dashboard.js'),
  login:          () => import('./pages/login.js'),
  tasks:          () => import('./pages/tasks.js'),
  workload:       () => import('./pages/workload.js'),
  planning:       () => import('./pages/planning.js'),
  worklist:       () => import('./pages/worklist.js'),
  myWork:         () => import('./pages/my-work.js'),
  campaigns:      () => import('./pages/campaigns.js'),
  archive:        () => import('./pages/archive.js'),
  campaignDetail: () => import('./pages/campaign-detail.js'),
  campaignForm:   () => import('./pages/campaign-form.js'),
  plans:          () => import('./pages/plans.js'),
  planDetail:     () => import('./pages/plan-detail.js'),
  planForm:       () => import('./pages/plan-form.js'),
  budgets:        () => import('./pages/budgets.js'),
  budgetForm:     () => import('./pages/budget-form.js'),
  assets:         () => import('./pages/assets.js'),
  documents:      () => import('./pages/documents.js'),
  platforms:      () => import('./pages/platforms.js'),
  crmContacts:    () => import('./pages/crm-contacts.js'),
  crmFlows:       () => import('./pages/crm-flows.js'),
  webDashboard:   () => import('./pages/web-dashboard.js'),
  marketIntel:    () => import('./pages/market-intel.js'),
  showRoi:        () => import('./pages/show-roi.js'),
  settings:       () => import('./pages/settings.js'),
  templateEditor: () => import('./pages/template-editor.js'),
  gtmAdmin:       () => import('./pages/gtm-admin.js'),
  forms:          () => import('./pages/forms.js'),
  formBuilder:    () => import('./pages/form-builder.js'),
  formSubmissions: () => import('./pages/form-submissions.js'),
  // ⚠️ ~2.4MB (GrapesJS + MJML). Lazy ONLY — never static-import these.
  emailTemplates: () => import('./pages/email-templates.js'),
  emailBuilder:   () => import('./pages/email-builder.js'),
};

/**
 * Render the portal shell.
 */
function renderAppShell() {
  const app = document.getElementById('app');
  app.innerHTML = '';

  const stripe = document.createElement('div');
  stripe.className = 'accent-stripe';
  app.appendChild(stripe);

  const layout = document.createElement('div');
  layout.className = 'app-layout';

  const mobileHeader = renderMobileHeader({ appTitle: 'ISG Marketing' });
  const overlay = createSidebarOverlay();

  const sidebar = renderNavigation({
    navConfig: NAV_CONFIG,
    appTitle: 'Marketing',
    fallbackInitial: 'M',
    // 🔴 Without appSlug, ui-kit falls back to auth.isAdmin() — the GLOBAL
    // platform-admin flag — so every `adminOnly` nav item is hidden from
    // marketing's own app-role admins. This app's only marketing=admin holds
    // platform role 'viewer', so Settings and GTM Admin were invisible to the
    // one person meant to use them, with the pages themselves reachable by URL.
    appSlug: 'marketing',
  });

  const mainWrapper = document.createElement('div');
  mainWrapper.style.cssText = 'display:flex;flex-direction:column;flex:1;min-width:0;';

  const toolbar = document.createElement('div');
  toolbar.className = 'global-toolbar';
  toolbar.style.cssText = 'display:flex;align-items:center;justify-content:flex-end;padding:0 var(--space-4);height:48px;border-bottom:1px solid var(--border-secondary);background:var(--surface-primary);gap:var(--space-2);flex-shrink:0;';

  const bellBtn = createNotificationBell();
  toolbar.appendChild(bellBtn);

  const main = document.createElement('main');
  main.className = 'main-content';
  main.id = 'main-content';

  mainWrapper.appendChild(toolbar);
  mainWrapper.appendChild(main);

  const notifSidebar = createNotificationSidebar();

  layout.appendChild(mobileHeader);
  layout.appendChild(overlay);
  layout.appendChild(sidebar);
  layout.appendChild(mainWrapper);
  app.appendChild(layout);
  app.appendChild(notifSidebar);

}

/**
 * Render a page module into #main-content.
 */
async function renderPage(loader, params = {}) {
  const main = document.getElementById('main-content');
  if (!main) return;

  main.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:50vh">
      <div class="spinner"></div>
    </div>
  `;

  try {
    const module = await loader();
    main.innerHTML = '';
    await module.render(main, params);
  } catch (err) {
    console.error('Page render error:', err);
    main.innerHTML = `
      <div class="main-content-inner">
        <div class="empty-state">
          <p style="color: var(--red-600); font-weight: 500;">Something went wrong</p>
          <p>${(err.message || 'Failed to load page.').replace(/[<>&"']/g, c => ({'<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;',"'":'&#39;'}[c]))}</p>
          <button class="btn btn-primary mt-4" onclick="location.reload()">Reload</button>
        </div>
      </div>
    `;
  }
}

/**
 * Initialize the application.
 */
async function init() {
  // A visitor's document folder (/d/<token>) — standalone, no auth, no shell.
  // The pack email links here, so it must render for somebody who has never
  // signed in to anything.
  if (window.location.pathname.startsWith('/d/')) {
    await loadBranding().catch(() => {});
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main"></main>';
    const mod = await import('./pages/public-documents.js');
    await mod.render(document.getElementById('main-content'));
    applyBranding();
    return;
  }

  // Consent link from a CRM flow email (/c/<token>) — standalone, no auth. The
  // customer has never signed in to anything; the token is the credential.
  if (window.location.pathname.startsWith('/c/')) {
    await loadBranding().catch(() => {});
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main"></main>';
    const mod = await import('./pages/public-consent.js');
    await mod.render(document.getElementById('main-content'));
    return;
  }

  // Public visitor form (/f/<slug>) — standalone, no auth, no app shell.
  if (window.location.pathname.startsWith('/f/')) {
    await loadBranding().catch(() => {});
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main"></main>';
    const mod = await import('./pages/public-form.js');
    await mod.render(document.getElementById('main-content'));
    applyBranding();
    return;
  }
  await loadBranding().catch(() => {});
  const hasSession = await auth.checkSession();

  // Define routes
  router
    .on('/', (params) => renderPage(pages.dashboard, params))
    .on('/tasks', (params) => renderPage(pages.tasks, params))
    .on('/workload', (params) => renderPage(pages.workload, params))
    .on('/plan', (params) => renderPage(pages.planning, params))
    .on('/worklist', (params) => renderPage(pages.worklist, params))
    .on('/my-work', (params) => renderPage(pages.myWork, params))
    .on('/campaigns', (params) => renderPage(pages.campaigns, params))
    .on('/archive', (params) => renderPage(pages.archive, params))
    .on('/campaigns/new', (params) => renderPage(pages.campaignForm, params))
    .on('/campaigns/:id', (params) => renderPage(pages.campaignDetail, params))
    .on('/campaigns/:id/edit', (params) => renderPage(pages.campaignForm, params))
    .on('/plans', (params) => renderPage(pages.plans, params))
    .on('/plans/new', (params) => renderPage(pages.planForm, params))
    .on('/plans/:id', (params) => renderPage(pages.planDetail, params))
    .on('/plans/:id/edit', (params) => renderPage(pages.planForm, params))
    .on('/budgets', (params) => renderPage(pages.budgets, params))
    .on('/budgets/new', (params) => renderPage(pages.budgetForm, params))
    .on('/budgets/:id/edit', (params) => renderPage(pages.budgetForm, params))
    .on('/assets', (params) => renderPage(pages.assets, params))
    .on('/documents', (params) => renderPage(pages.documents, params))
    .on('/platforms', (params) => renderPage(pages.platforms, params))
    .on('/crm/contacts', (params) => renderPage(pages.crmContacts, params))
    .on('/crm/flows', (params) => renderPage(pages.crmFlows, params))
    .on('/crm/flows/:id', (params) => renderPage(pages.crmFlows, params))
    // Retired 2026-10-12 — folded into Marketing Analytics tabs; old links land there.
    .on('/attribution', () => { location.replace('/web-dashboard?tab=attribution'); })
    .on('/web-dashboard', (params) => renderPage(pages.webDashboard, params))
    .on('/market-intel', (params) => renderPage(pages.marketIntel, params))
    .on('/form-requests', () => { location.replace('/web-dashboard?tab=enquiries'); })
    .on('/reports', () => { location.replace('/budgets'); })   // retired 2026-10-12: Budget vs Actual lives in Budget Manager
    .on('/shows', (params) => renderPage(pages.showRoi, params))
    .on('/show-roi', () => { location.replace('/shows'); })
    .on('/forms', (params) => renderPage(pages.forms, params))
    .on('/forms/new', (params) => renderPage(pages.formBuilder, params))
    .on('/forms/:id/edit', (params) => renderPage(pages.formBuilder, params))
    .on('/forms/:id/submissions', (params) => renderPage(pages.formSubmissions, params))
    .on('/email-templates', (params) => renderPage(pages.emailTemplates, params))
    .on('/email-templates/new', (params) => renderPage(pages.emailBuilder, params))
    .on('/email-templates/:id/edit', (params) => renderPage(pages.emailBuilder, params))
    .on('/settings', (params) => renderPage(pages.settings, params))
    .on('/settings/templates', (params) => renderPage(pages.templateEditor, params))
    .on('/settings/templates/:id', (params) => renderPage(pages.templateEditor, params))
    .on('/gtm', (params) => renderPage(pages.gtmAdmin, params))
    .on('/login', (params) => renderPage(pages.login, params));

  // Auth guard
  router.before((path) => {
    // An AUTHENTICATED user must never be left on the login page. Waving
    // /login through unconditionally renders the login FORM inside the app
    // shell — the app looks signed in and signed out at once, and only a
    // manual reload clears it. Not an edge case: api.js bounces here on any
    // 401 while the httpOnly cookie usually survives, so the reload lands
    // authenticated; the SSO broker's ?next and plain bookmarks reach it too.
    // Fixed in isg-app-template 2026-07-18; back-propagated 2026-07-31.
    if (path === '/login' && store.get('isAuthenticated')) {
      router.navigate('/');
      return false;
    }
    if (path === '/login') return true;
    if (!store.get('isAuthenticated')) {
      router.navigate('/login');
      return false;
    }
    return true;
  });

  // Apply branding after every route change
  router.after(() => {
    setTimeout(() => applyBranding(), 50);
  });

  if (!hasSession) {
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main"></main>';
    const currentPath = window.location.pathname;
    if (currentPath === '/login') {
      await router.resolve();
    } else {
      const module = await pages.login();
      module.render(document.getElementById('main-content'));
      window.history.replaceState(null, '', '/login');
    }
    applyBranding();
  } else {
    renderAppShell();
    checkEnvBanner(); // beta/test banner (no-op on live)
    await router.resolve();
    applyBranding();

    // Marketing Assistant — floats on every page for authenticated users.
    const { renderAIAssistant } = await import('./components/ai-assistant-widget.js');
    const aiMount = document.createElement('div');
    aiMount.id = 'ai-assistant-mount';
    document.body.appendChild(aiMount);
    renderAIAssistant(aiMount);
  }
}

// Boot
init().catch((err) => {
  console.error('Failed to initialize app:', err);
  document.getElementById('app').innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;flex-direction:column;gap:1rem;">
      <p>Failed to load the application. Please refresh.</p>
      <button class="btn btn-primary" onclick="location.reload()">Reload</button>
    </div>
  `;
});
